import React from 'react';
import { useMyBloodRequests } from '../hooks/useSupabase';

const MyBloodRequests = ({ requestedBy }) => {
  const { data: requests, loading } = useMyBloodRequests(requestedBy);

  const getUrgencyColor = (level) => {
    switch (level) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'fulfilled': return 'bg-blue-100 text-blue-800';
      case 'expired': return 'bg-gray-100 text-gray-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (!requestedBy) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <p className="text-yellow-800">Please register or login to view your blood requests.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="animate-pulse space-y-4">
          {[1, 2].map(i => (
            <div key={i} className="bg-gray-200 h-48 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">My Blood Requests</h1>
        <p className="text-gray-600">View and manage your blood donation requests</p>
      </div>

      {requests?.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">🩸</div>
          <h3 className="text-xl font-medium text-gray-800 mb-2">No Blood Requests</h3>
          <p className="text-gray-600 mb-4">You haven't created any blood requests yet.</p>
          <a
            href="/request-blood"
            className="inline-block px-6 py-3 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            Create Blood Request
          </a>
        </div>
      ) : (
        <div className="space-y-6">
          {requests.map((request) => (
            <div
              key={request.id}
              className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-semibold text-gray-800">
                      {request.patientName}
                    </h3>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getUrgencyColor(request.urgencyLevel)}`}>
                      {request.urgencyLevel.charAt(0).toUpperCase() + request.urgencyLevel.slice(1)} Priority
                    </span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(request.status)}`}>
                      {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm">
                    Created: {new Date(request.createdAt).toLocaleDateString('en-IN', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
                
                <div className="text-right">
                  <div className="text-3xl font-bold text-red-600 mb-1">
                    {request.bloodGroup}
                  </div>
                  <div className="text-sm text-gray-600">
                    {request.unitsRequired} unit{request.unitsRequired > 1 ? 's' : ''}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                <div>
                  <h4 className="font-medium text-gray-800 mb-2">Hospital Information</h4>
                  <div className="space-y-1 text-sm text-gray-600">
                    <p><span className="font-medium">Name:</span> {request.hospital.name}</p>
                    <p><span className="font-medium">Location:</span> {request.hospital.location}</p>
                    <p><span className="font-medium">District:</span> {request.hospital.district}</p>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-800 mb-2">Contact Information</h4>
                  <div className="space-y-1 text-sm text-gray-600">
                    <p><span className="font-medium">Contact Person:</span> {request.contactPerson.name}</p>
                    <p><span className="font-medium">Phone:</span> {request.contactPerson.phoneNumber}</p>
                    <p><span className="font-medium">Relationship:</span> {request.contactPerson.relationship}</p>
                    {request.requiredByDate && (
                      <p><span className="font-medium">Required By:</span> {new Date(request.requiredByDate).toLocaleDateString('en-IN', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Accepted Donors Section */}
              {request.acceptedDonors && request.acceptedDonors.length > 0 && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
                  <h4 className="text-green-800 font-semibold mb-3">
                    ✓ {request.acceptedDonors.length} Donor{request.acceptedDonors.length > 1 ? 's' : ''} Ready to Donate
                  </h4>
                  <div className="space-y-3">
                    {request.acceptedDonors.map((donor, index) => (
                      <div key={index} className="bg-white p-3 rounded border border-green-100">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-medium text-gray-900">{donor.name}</p>
                              {donor.isRegistered && (
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                                  Registered Donor
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-600 space-y-1">
                              <p>
                                📞 <a href={`tel:${donor.phoneNumber}`} className="text-blue-600 hover:underline font-medium">
                                  {donor.phoneNumber}
                                </a>
                              </p>
                              <p>📍 {donor.location}, {donor.district}</p>
                              {donor.bloodGroup && <p>🩸 Blood Group: <span className="font-medium">{donor.bloodGroup}</span></p>}
                            </div>
                          </div>
                          <div className="text-xs text-gray-500 text-right">
                            {donor.acceptedAt && (
                              <>
                                <p>Accepted on:</p>
                                <p>{new Date(donor.acceptedAt).toLocaleString('en-IN', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}</p>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                    <strong>Important:</strong> Please contact the donors directly to coordinate the donation. Remember to mark the request as fulfilled once completed.
                  </div>
                </div>
              )}

              {/* Interested Donors */}
              {request.interestedDonors && request.interestedDonors.length > 0 && (
                <div className="mt-3 p-3 bg-blue-50 rounded-md border border-blue-200">
                  <p className="text-sm text-blue-700">
                    <span className="font-medium">{request.interestedDonors.length}</span> donor{request.interestedDonors.length > 1 ? 's' : ''} {request.interestedDonors.length > 1 ? 'have' : 'has'} expressed interest
                  </p>
                </div>
              )}

              {request.additionalNotes && (
                <div className="mt-4 p-3 bg-gray-50 rounded-md">
                  <h4 className="font-medium text-gray-800 mb-1">Additional Notes</h4>
                  <p className="text-sm text-gray-600">{request.additionalNotes}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyBloodRequests;
